import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvDateParser from '../UI/atoms/OvDateParser';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvRecordDetailsCanvas from '../UI/atoms/OvRecordDetailsCanvas';
import OvStatusChip from '../UI/atoms/OvStatusChip';
import OvCopyToClipboardButton from '../UI/molecules/OvCopyToClipboardButton';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvRecordDetailsSection from '../UI/molecules/OvRecordDetailsSection';
import OvRecordHeaderActions from '../UI/molecules/OvRecordHeaderActions';
import OvRecordDetailsHeader from '../UI/organisms/OvRecordDetailsHeader';
import breakpoints from '../../design-system/breakpoints';
import Colours from '../../design-system/colours';
import { useNavigate, useParams } from 'react-router-dom';
import Variables from '../../design-system/variables';
import OvButton from '../UI/atoms/OvButton';
import OvTable from '../UI/molecules/OvTable';
import OvQrCodeImage from '../UI/molecules/OvQrCodeImage';
import OvConfirmationDialog from '../UI/molecules/OvConfirmationDialog';
import LotsService from '../../services/lots.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import {
  CalibrationCartridgeType,
  getCalibrationResult,
} from '../../common/utils/services/lot-calibration-utils';
import OvHormoneCalibration, {
  CalibrationFormValue,
} from '../UI/organisms/OvHormoneCalibration';
import { LotDataUtils } from '../../common/utils/services/lot-data-utils';
import { LotFields } from '../../firebase/document-field.enums';
import {
  Lot,
  ToolkitAlgoParams,
  toolkitAlgoParamsDescriptions,
} from '../../common/model/dto/lot';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import { DownloadUtils } from '../../common/utils/services/download-utils';
import { elementToSVG } from 'dom-to-svg';
import StringUtils from '../../common/utils/services/string-utils';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import { useBeforeunload } from 'react-beforeunload';
import { TableRowEditWidgetType } from '../../common/model/ui/table-row-edit-widget.type';
import OvBackButton from '../UI/atoms/OvBackButton';
import { Resource } from '../../common/model/type/resource.enum';
import useAuthorized from '../../hooks/use-authorized';
import JSONInput from 'react-json-editor-ajrm';
// @ts-ignore
import locale from 'react-json-editor-ajrm/locale/en';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import OvLotCalibrationSection from '../UI/organisms/OvLotCalibrationSection';
import { useNavigationPrompt } from '../../hooks/use-navigation-prompt';

const LotDetails: FC = () => {
  let { lotId }: any = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const lotQueryKey = ['admin-lot', lotId];

  const applicationOperations = useAuthorized(Resource.Application);
  const lotOperations = useAuthorized(Resource.Lot);

  const [calibratedSelectedLot, setCalibratedSelectedLot] =
    useState<Lot | null>(null);
  const [calibrationErrors, setCalibrationErrors] = useState<
    Partial<Record<CalibrationCartridgeType, string>>
  >({});
  useNavigationPrompt(!!calibratedSelectedLot, t('common.leavePrompt'));

  const {
    data: savedLot,
    isFetching: isLotFetching,
    error: lotError,
    refetch: refetchLot,
  } = useQuery({
    queryKey: lotQueryKey,
    queryFn: () => LotsService.getById(lotId),
    enabled: !!lotId,
  });
  const lot: Lot | undefined = savedLot && {
    ...savedLot,
    ...calibratedSelectedLot,
  };

  const setSavedLot = (updatedLot: Lot) =>
    queryClient.setQueryData<Lot>(lotQueryKey, (previousLot) => ({
      ...previousLot,
      ...updatedLot,
    }));

  const updateLotMutation = useMutation({
    mutationFn: (updatedLot: Lot) => LotsService.updateLot(lotId, updatedLot),
    onSuccess: setSavedLot,
  });

  const saveCalibrationMutation = useMutation({
    mutationFn: (updatedLot: Lot) => LotsService.updateLot(lotId, updatedLot),
    onSuccess: (updatedLot) => {
      setSavedLot(updatedLot);
      setCalibratedSelectedLot(null);
    },
  });

  const deleteLotMutation = useMutation({
    mutationFn: (id: string) => LotsService.deleteLot(id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: lotQueryKey });
      queryClient.invalidateQueries({ queryKey: ['admin-lots'] });
      navigate('/lots');
    },
  });

  const calibrationMutation = useMutation({
    mutationFn: LotsService.runCalibrationProcess,
    onMutate: ({ cartridge_type }) => {
      if (cartridge_type) {
        setCalibrationErrors((errors) => ({
          ...errors,
          [cartridge_type]: '',
        }));
      }
    },
    onSuccess: (response, { cartridge_type }) => {
      if (!cartridge_type) {
        return;
      }

      const result = getCalibrationResult(cartridge_type, response);

      switch (result.status) {
        case 'calibrated':
          setCalibratedSelectedLot((previousCalibration) => ({
            ...previousCalibration,
            ...result.lotFields,
          }));
          break;
        case 'failed':
          setCalibrationErrors((errors) => ({
            ...errors,
            [cartridge_type]: result.errorMessage,
          }));
          break;
        case 'ignored':
          break;
        default: {
          const exhaustiveCheck: never = result;
          return exhaustiveCheck;
        }
      }
    },
    onError: (error, { cartridge_type }) => {
      if (cartridge_type) {
        setCalibrationErrors((errors) => ({
          ...errors,
          [cartridge_type]: getAxiosErrorMessage(error),
        }));
      }
    },
  });

  const isLoading =
    isLotFetching ||
    updateLotMutation.isPending ||
    saveCalibrationMutation.isPending ||
    deleteLotMutation.isPending ||
    calibrationMutation.isPending;
  const error =
    lotError ??
    updateLotMutation.error ??
    saveCalibrationMutation.error ??
    deleteLotMutation.error;

  const [isOpenDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] =
    useState<boolean>(false);

  const [toolkitAlgoParamsJson, setToolkitAlgoParamsJson] =
    useState<ToolkitAlgoParams | null>({});
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('info');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useBeforeunload(
    !!calibratedSelectedLot ? (event) => event.preventDefault() : undefined
  );

  useEffect(() => {
    if (savedLot) {
      setToolkitAlgoParamsJson(savedLot.toolkit_algo_parameters || {});
    }
  }, [savedLot]);

  const handleToolkitAlgoParamsChange = (value: any) => {
    if (value.jsObject !== undefined) {
      setToolkitAlgoParamsJson(value.jsObject);
    }
  };

  const handleToolkitAlgoParamsUpdate = async () => {
    if (lot?.id && toolkitAlgoParamsJson) {
      try {
        await updateLotMutation.mutateAsync({
          toolkit_algo_parameters: toolkitAlgoParamsJson,
        });

        // Show success message
        setSnackbarMessage(t('lots.toolkitAlgoParametersUpdated'));
        setSnackbarSeverity('success'); // Set severity to 'success'
        setOpenSnackbar(true);
      } catch (error) {
        // Show error message
        setSnackbarMessage((error as any).message || t('common.updateFailed'));
        setSnackbarSeverity('error'); // Set severity to 'error'
        setOpenSnackbar(true);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarMessage(null);
    setOpenSnackbar(false);
  };

  const openDeleteConfirmationDialog = () =>
    setOpenDeleteConfirmationDialog(true);

  const handleDelete = () => {
    setOpenDeleteConfirmationDialog(false);
    deleteLotMutation.mutate(
      lot?.document_id ? lot?.document_id : (lot?.id as string)
    );
  };

  const formatUpdatedProperties = (updatedProperties: any) => {
    const [prop, nestedProp] = Object.keys(updatedProperties)[0]?.split('.');

    if (!!updatedProperties[LotFields.ax_array]) {
      updatedProperties[LotFields.ax_array] =
        StringUtils.convertStringToNumberArray(
          updatedProperties[LotFields.ax_array]
        );
    }

    if (nestedProp) {
      updatedProperties[prop] = {
        // @ts-ignore
        ...lot[prop],
        [nestedProp]:
          nestedProp === LotFields.x || nestedProp === LotFields.raw_x
            ? StringUtils.convertStringToNumberArray(
                updatedProperties[`${prop}.${nestedProp}`]
              )
            : updatedProperties[`${prop}.${nestedProp}`],
      };
    }
  };

  const handleLotUpdate = (updatedProperties: any) => {
    if (lot?.id) {
      formatUpdatedProperties(updatedProperties);

      updateLotMutation.mutate({
        ...updatedProperties,
      });
    }
  };

  const saveUpdatedModelFitAndCalibrationParams = (updateLot: Lot) => {
    if (lot?.id) {
      saveCalibrationMutation.mutate(updateLot);
    }
  };

  const handleCalibration = (
    calibrationValue: CalibrationFormValue,
    cartridge_type: 'lr_e3g' | 'lh' | 'pg' | 'testosterone'
  ) => {
    const concentrations = StringUtils.convertStringToNumberArray(
      calibrationValue.concentrations
    );
    const signals = StringUtils.convertStringToNumberArray(
      calibrationValue.signals
    );

    calibrationMutation.mutate({
      fit_type: calibrationValue.fit_type,
      concentrations,
      signals,
      cartridge_type,
      should_fix_min_at_zero: calibrationValue.should_fix_min_at_zero,
    });
  };

  const handleQrCodeDownload = () => {
    const svgElement = elementToSVG(
      document.querySelector('.qr-code-container') as Element
    );

    DownloadUtils.downloadSvg(
      svgElement,
      lot?.document_id ? lot?.document_id : (lot?.id as string)
    );
  };

  const lotDeleteAllowed =
    lotOperations.delete || applicationOperations.supervise;
  const lotUpdateAllowed =
    lotOperations.update || applicationOperations.supervise;

  if (!lot) {
    return (
      <Page>
        {lotError ? (
          <OvRecordDetailsCanvas>
            <OvListStateMessage
              isAlert
              icon={<WarningIcon />}
              title={t('lots.details.errorTitle')}
              description={getAxiosErrorMessage(lotError)}
            >
              <StateActions>
                <OvBackButton label={t('lots.title')} />
                <OvCompactButton onClick={() => refetchLot()}>
                  {t('common.actions.retry')}
                </OvCompactButton>
              </StateActions>
            </OvListStateMessage>
          </OvRecordDetailsCanvas>
        ) : (
          <OvLoadingIndicator position="fixed" />
        )}
      </Page>
    );
  }

  const lotDisplayId = (lot.document_id ?? lot.id) as string;

  return (
    <Page aria-busy={isLoading}>
      {error && <OvErrorMessage message={getAxiosErrorMessage(error)} />}

      <OvRecordDetailsCanvas>
        <OvRecordDetailsHeader
          breadcrumbLabel={t('lots.details.breadcrumb')}
          backControl={<OvBackButton label={t('lots.title')} />}
          current={lotDisplayId}
          title={lot.name || lotDisplayId}
          titleAside={
            lot.status && <OvStatusChip label={lot.status} tone="neutral" />
          }
          meta={
            <>
              <span>
                {lotDisplayId}
                <OvCopyToClipboardButton
                  text={lotDisplayId}
                  label={t('lots.details.copyId')}
                />
              </span>
              {lot.type && (
                <span>{LotDataUtils.getLotTypeLabel(lot.type)}</span>
              )}
              {lot.created_at && (
                <span>
                  <OvDateParser
                    date={lot.created_at}
                    format="MMM DD, YYYY - hh:mm A"
                  />
                </span>
              )}
            </>
          }
          actions={
            lotDeleteAllowed && (
              <OvRecordHeaderActions>
                <OvCompactButton
                  tone="danger"
                  icon={<DeleteIcon />}
                  onClick={openDeleteConfirmationDialog}
                >
                  {t('common.actions.delete')}
                </OvCompactButton>
              </OvRecordHeaderActions>
            )
          }
        />

        <Body>
          <OvRecordDetailsSection title={t('lots.details.qrSection')}>
            <div className="qr-code-container">
              <OvQrCodeImage options={{ value: lotDisplayId }} />

              <QrCodeLabel>
                {LotDataUtils.getLotTypeLabel(lot.type)}
              </QrCodeLabel>
            </div>

            <CtaContainer>
              <OvCompactButton
                tone="secondary"
                icon={<CloudDownloadIcon />}
                onClick={handleQrCodeDownload}
              >
                {t('common.downloadQrCode')}
              </OvCompactButton>
            </CtaContainer>
          </OvRecordDetailsSection>

          <DetailsArea>
            <OvRecordDetailsSection title={t('lots.details.detailsSection')}>
              <OvTable
                data={LotDataUtils.mapLotToTable(lot)}
                theme="Light"
                onSaveRowValue={handleLotUpdate}
                editable={lotUpdateAllowed}
              ></OvTable>
            </OvRecordDetailsSection>
          </DetailsArea>
        </Body>

        <CalibrationArea>
          <OvRecordDetailsSection title={t('lots.details.calibrationSection')}>
            <Wrapper>
              <StyledAccordion>
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="lh"
                  id="lh"
                >
                  <StyledTitle>{t('common.luteinizing')}</StyledTitle>
                </StyledAccordionSummary>
                <AccordionDetails>
                  <OvHormoneCalibration
                    initialCalibrationValue={lot.lh_calibration}
                    initialHormoneModelFitParameters={{
                      ...lot.lh_model_fit_parameters,
                      ...calibratedSelectedLot?.lh_model_fit_parameters,
                    }}
                    errorMessage={calibrationErrors.lh ?? ''}
                    runModel={(values) => handleCalibration(values, 'lh')}
                    saveChanges={saveUpdatedModelFitAndCalibrationParams}
                    type="lh"
                    unit={lot?.lh_unit}
                    ax_array={lot?.ax_array}
                  />

                  <OvTable
                    data={[
                      {
                        label: 'lots.calibration.fields.unit',
                        translateLabel: true,
                        value: lot?.lh_unit,
                        prettifiedValue: lot?.lh_unit || '-',
                        editOptions: {
                          propertyName: LotFields.lh_unit,
                          widgetType: TableRowEditWidgetType.TEXT,
                        },
                      },
                    ]}
                    theme="Light"
                    onSaveRowValue={handleLotUpdate}
                  ></OvTable>
                </AccordionDetails>
              </StyledAccordion>
            </Wrapper>

            <Wrapper>
              <StyledAccordion>
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="pg"
                  id="pg"
                >
                  <StyledTitle>{t('common.progesterone')}</StyledTitle>
                </StyledAccordionSummary>
                <AccordionDetails>
                  <OvHormoneCalibration
                    initialCalibrationValue={lot.pg_calibration}
                    initialHormoneModelFitParameters={{
                      ...lot.pg_model_fit_parameters,
                      ...calibratedSelectedLot?.pg_model_fit_parameters,
                    }}
                    errorMessage={calibrationErrors.pg ?? ''}
                    runModel={(values) => handleCalibration(values, 'pg')}
                    saveChanges={saveUpdatedModelFitAndCalibrationParams}
                    type="pg"
                    unit={lot?.pg_unit}
                    ax_array={lot?.ax_array}
                  />

                  <OvTable
                    data={[
                      {
                        label: 'lots.calibration.fields.unit',
                        translateLabel: true,
                        value: lot?.pg_unit,
                        prettifiedValue: lot?.pg_unit || '-',
                        editOptions: {
                          propertyName: LotFields.pg_unit,
                          widgetType: TableRowEditWidgetType.TEXT,
                        },
                      },
                    ]}
                    theme="Light"
                    onSaveRowValue={handleLotUpdate}
                  ></OvTable>
                </AccordionDetails>
              </StyledAccordion>
            </Wrapper>

            <Wrapper>
              <StyledAccordion>
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="lr_e3g"
                  id="lr_e3g"
                >
                  <StyledTitle>{t('common.lowRangeEstrogen')}</StyledTitle>
                </StyledAccordionSummary>
                <AccordionDetails>
                  <OvHormoneCalibration
                    initialCalibrationValue={lot.lr_e3g_calibration}
                    initialHormoneModelFitParameters={{
                      ...lot.lr_e3g_model_fit_parameters,
                      ...calibratedSelectedLot?.lr_e3g_model_fit_parameters,
                    }}
                    errorMessage={calibrationErrors.lr_e3g ?? ''}
                    runModel={(values) => handleCalibration(values, 'lr_e3g')}
                    saveChanges={saveUpdatedModelFitAndCalibrationParams}
                    type="lr_e3g"
                    unit={lot?.lr_e3g_unit}
                    ax_array={lot?.ax_array}
                  />

                  <OvTable
                    data={[
                      {
                        label: 'lots.calibration.fields.unit',
                        translateLabel: true,
                        value: lot?.lr_e3g_unit,
                        prettifiedValue: lot?.lr_e3g_unit || '-',
                        editOptions: {
                          propertyName: LotFields.lr_e3g_unit,
                          widgetType: TableRowEditWidgetType.TEXT,
                        },
                      },
                    ]}
                    theme="Light"
                    onSaveRowValue={handleLotUpdate}
                  ></OvTable>
                </AccordionDetails>
              </StyledAccordion>
            </Wrapper>

            <Wrapper>
              <StyledAccordion>
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="toolkit-algo-parameters"
                  id="toolkit-algo-parameters"
                >
                  <StyledTitle>{t('lots.toolkitAlgoParameters')}</StyledTitle>
                </StyledAccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* JSON Editor */}
                    <Grid item xs={12} md={6}>
                      <JsonContainer>
                        {!!lot && (
                          <JSONInput
                            id="toolkit_algo_parameters_editor"
                            placeholder={lot.toolkit_algo_parameters || {}}
                            locale={locale}
                            theme="light_mitsuketa_tribute"
                            height="500px"
                            width="100%"
                            onChange={handleToolkitAlgoParamsChange}
                          />
                        )}
                      </JsonContainer>
                      <StyledOvButton onClick={handleToolkitAlgoParamsUpdate}>
                        {t('common.actions.save')}
                      </StyledOvButton>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <InfoContainer>
                        <Typography variant="h6" gutterBottom>
                          Available Parameters
                        </Typography>
                        <ParameterList>
                          {toolkitAlgoParamsDescriptions.map((param) => (
                            <li key={param.key}>
                              <strong>{param.key}</strong>: {param.description}
                            </li>
                          ))}
                        </ParameterList>
                      </InfoContainer>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </StyledAccordion>
            </Wrapper>

            {lot?.id && (
              <Wrapper>
                <StyledAccordion>
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="calibration-visualization"
                    id="calibration-visualization"
                  >
                    <StyledTitle>
                      {t('lots.calibration.visualization.title')}
                    </StyledTitle>
                  </StyledAccordionSummary>
                  <AccordionDetails>
                    <OvLotCalibrationSection
                      lotId={lot.id}
                      lhUnit={lot.lh_unit}
                      pgUnit={lot.pg_unit}
                      lrE3gUnit={lot.lr_e3g_unit}
                    />
                  </AccordionDetails>
                </StyledAccordion>
              </Wrapper>
            )}
          </OvRecordDetailsSection>
        </CalibrationArea>
      </OvRecordDetailsCanvas>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <OvConfirmationDialog
        isOpen={isOpenDeleteConfirmationDialog}
        onCancel={() => setOpenDeleteConfirmationDialog(false)}
        onConfirm={handleDelete}
        description={'lots.confirmDelete'}
        title={t('common.areYouSure')}
      />

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </Page>
  );
};

export default LotDetails;

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.375rem;
  margin-left: 1rem;
  min-width: 0;
  color: ${Colours.OV_BASE};
`;

const StateActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(16rem, auto) minmax(0, 1fr);

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const DetailsArea = styled.div`
  min-width: 0;
  border-left: 1px solid ${Colours.OV_BORDER_COLOR_V3};

  @media (max-width: ${breakpoints.md}) {
    border-left: 0;
    border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  }
`;

const CalibrationArea = styled.div`
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const QrCodeLabel = styled.p`
  font-size: ${Variables.fontSizes.XXLARGE};
  line-height: ${Variables.fontSizes.XXLARGE};
  font-family: 'CentraNo2-Medium';
  margin: 0.875rem 0;
  text-align: center;
  color: black;
`;

const CtaContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const Wrapper = styled.div`
  padding: 0.5rem 0;
`;

const StyledTitle = styled.span`
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
`;

const StyledAccordion = styled(Accordion)`
  && {
    box-shadow: none;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  && {
    background-color: ${Colours.OV_LIGHT_GRAY};
    border-radius: 0.25rem;
    align-items: center;

    .MuiAccordionSummary-content {
      align-items: center;
    }
  }
`;

const JsonContainer = styled.div`
  padding-bottom: 1.5rem;
`;

const StyledOvButton = styled(OvButton)`
  && {
    padding: 0.25rem 1rem;
    text-transform: none;
    border-radius: 0.75rem;
  }
`;

const InfoContainer = styled.div`
  padding: 1rem;
  background-color: ${Colours.GRAY['100']};
  border-radius: ${Variables.borderRadius.SMALL};
`;

const ParameterList = styled.ul`
  list-style-type: none;
  padding-left: 0;

  li {
    margin-bottom: 0.5rem;
  }
`;
